import styled from '@emotion/styled';
import { useEffect, useState, useCallback } from 'react';
import { SubwayStation } from '@/api/repository/types/types';
import StationSearchField from '@/components/common/station/StationSearchField';

import { designSystem } from '@/styles/theme';

interface RegionSelectorProps {
  onSelectedSubwayItem: (item: string[]) => void;
  defaultSelectedSubwayStations?: SubwayStation[];
  helperText?: string | null;
}

// HelperText + 역 검색 칸이 부모 flex 의 형제로 펼쳐지지 않도록 column 컨테이너로 감싼다.
// 모바일/PC 어디서나 HelperText 가 상단, 역 칩이 하단으로 일관 배치된다.
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

const HelperText = styled.p`
  font-size: 0.85rem;
  color: ${designSystem.colors.primary};
  background: ${designSystem.colors.primaryLight};
  border: 1px solid ${designSystem.colors.primary};
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 10px 0;
`;

const DEFAULT_HELPER_TEXT = '💡 자주 이용하는 지하철역을 선택하시면 거리 기반 맞춤 추천에 도움이 됩니다.';

/**
 * 가입·이력서·내 정보·치과 정보의 "인근 지하철역" 칸 — 전국 역을 이름으로 검색해 고른다(StationSearchField).
 * 부모에는 예전과 똑같이 고른 역 id 배열만 넘긴다(저장 요청 모양 그대로).
 */
const RegionSelector = ({
  onSelectedSubwayItem,
  defaultSelectedSubwayStations = [],
  helperText = DEFAULT_HELPER_TEXT,
}: RegionSelectorProps) => {
  const [selectedSubRegions, setSelectedSubRegions] = useState<SubwayStation[]>(
    defaultSelectedSubwayStations
  );

  // 부모(수정 화면)가 비동기로 데이터를 받아 defaultSelectedSubwayStations를 뒤늦게 채우는 경우에도
  // 화면에 반영되도록 prop 변경을 감지해 selectedSubRegions를 동기화한다.
  // 사용자가 직접 선택한 뒤에도 prop이 다시 들어오면 덮어쓰는 깜빡임을 방지하기 위해,
  // 현재 선택이 비어있고 prop에 값이 들어왔을 때만 초기화한다.
  useEffect(() => {
    if (selectedSubRegions.length === 0 && defaultSelectedSubwayStations.length > 0) {
      setSelectedSubRegions(defaultSelectedSubwayStations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedSubwayStations]);

  const memoizedOnSelectedSubwayItem = useCallback(onSelectedSubwayItem, []);

  useEffect(() => {
    const selectedIds = selectedSubRegions.map(subRegion => subRegion.id);
    memoizedOnSelectedSubwayItem(selectedIds);
  }, [selectedSubRegions, memoizedOnSelectedSubwayItem]);

  return (
    <Wrapper>
      {helperText && <HelperText>{helperText}</HelperText>}
      <StationSearchField value={selectedSubRegions} onChange={setSelectedSubRegions} />
    </Wrapper>
  );
};

export default RegionSelector;
